import fetch from "isomorphic-fetch"
import qs from "qs"
import { getApiKey } from "./apiKeys"
import { Config } from "./config"

type HttpRequestType = {
    method: string
    headers: Record<string, any>
    body?: Record<string, any>
    parameters?: Record<string, any>
    path?: string
    withResponseHeaders?: boolean
    useQueryStringArray?: boolean // Use bracket notation for arrays when true
}
export class NotAuthenticated {
    type = "NotAuthenticated"
    status = 403
}

export class NoAccess {
    type = "NoAccess"
    status = 403
}

export const request = (data: HttpRequestType): Promise<any> => {
    const options = {
        method: data.method,
        headers: {
            "Content-Type": "application/json",
            "figure-api-key": getApiKey(),
            ...data.headers,
        },
        body: data.body === null ? undefined : JSON.stringify(data.body),
        timeout: 30 * 1000,
    }
    const url: string = encodeUrlParameters(
        Config.webApiBaseUrl,
        data.path || "",
        data.parameters || {},
        data.useQueryStringArray
    ).replace(/\s/g, "")

    if (data.withResponseHeaders) {
        return fetch(url, options)
            .then(response => getResponseBodyAndHeaders(response))
            .catch(error => {
                return Promise.reject(new Error(error))
            })
    } else {
        return fetch(url, options)
            .then(response => {
                if (response.status >= 200 && response.status < 300) {
                    return getResponseBody(response)
                } else if (response.status === 403) {
                    return getResponseBody(response).then(body => {
                        if (body?.code === "NoAccess") {
                            return Promise.reject(new NoAccess())
                        }
                        return Promise.reject(new NotAuthenticated())
                    })
                } else {
                    return getResponseBody(response).then(body =>
                        Promise.reject({
                            status: response.status,
                            body: body,
                        })
                    )
                }
            })
            .catch(error => {
                return Promise.reject(error)
            })
    }
}

const getResponseBody = response => {
    if (response.headers.get("content-type").includes("json")) {
        return response.json().catch(() => Promise.resolve({}))
    } else if (response.headers.get("content-type").includes("text")) {
        return response.text().catch(() => Promise.resolve({}))
    } else if (response.headers.get("content-type").includes("pdf")) {
        return response.blob().catch(() => Promise.resolve({}))
    } else if (response.headers.get("content-type").includes("vnd.ms-excel")) {
        return response.arrayBuffer().catch(() => Promise.resolve({}))
    } else {
        return Promise.resolve({})
    }
}

const getResponseBodyAndHeaders = response => {
    return response.status >= 200 && response.status < 300
        ? getResponseBody(response).then(responseBody => ({
              responseBody: responseBody,
              responseHeaders: response.headers,
          }))
        : getResponseBody(response).then(responseBody =>
              Promise.reject(
                  // @ts-expect-error
                  new Error({
                      status: response.status,
                      body: responseBody,
                      responseHeaders: response.headers,
                  })
              )
          )
}

export const encodeUrlParameters = (
    baseUrl: string,
    path: string,
    parameters: Record<string, any>,
    useQueryStringArray?: boolean
): string => {
    if (Object.keys(parameters).length === 0) {
        return baseUrl + path
    }

    const queryString = qs.stringify(parameters, {
        arrayFormat: useQueryStringArray ? "brackets" : "comma",
        addQueryPrefix: false,
    })

    return baseUrl + path + "?" + queryString
}
